import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import {
  AccountHealthListResult,
  listAccountHealth,
} from '../../services/customerSuccessEngagementApi';

const PAGE_SIZE = 10;

function scoreTone(score: number): string {
  if (score >= 75) return 'text-emerald-700';
  if (score >= 50) return 'text-amber-700';
  return 'text-red-700';
}

export function AccountHealthPage() {
  const [data, setData] = useState<AccountHealthListResult | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const result = await listAccountHealth({ page, pageSize: PAGE_SIZE });
        setData(result);
        setPage(result.page);
      } catch {
        toast.error('Falha ao carregar saúde das contas.');
      } finally {
        setLoading(false);
      }
    })();
  }, [page]);

  const summary = data?.surveySummary;
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <PageShell>
      <PageHeader
        title="Saúde da Conta"
        description="Visão consolidada do risco e do engajamento das empresas com contrato ativo. O score combina chamados abertos, violações de SLA, médias de CSAT e proximidade da renovação. Use para priorizar contas que precisam de atenção antes que a satisfação ou a renovação sejam afetadas."
      />

      {loading && !data ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-4">
            <PageCard title="CSAT médio" subtitle={summary?.csatCount ? `${summary.csatCount} respostas` : 'Sem dados'}>
              <p className="text-2xl font-semibold text-secondary-900">
                {summary?.avgCsat != null ? summary.avgCsat.toFixed(1) : '—'}
              </p>
            </PageCard>
            <PageCard title="NPS médio" subtitle={summary?.npsCount ? `${summary.npsCount} respostas` : 'Sem dados'}>
              <p className="text-2xl font-semibold text-secondary-900">
                {summary?.avgNps != null ? summary.avgNps.toFixed(1) : '—'}
              </p>
            </PageCard>
            <PageCard title="Contas" subtitle="Com score calculado">
              <p className="text-2xl font-semibold text-secondary-900">{totalCount}</p>
            </PageCard>
            <PageCard title="Respostas prontas" subtitle="Biblioteca de atendimento">
              <Link to="/customer-success/engagement" className="text-sm text-primary-700 hover:underline">
                Abrir engajamento
              </Link>
            </PageCard>
          </div>

          {!data?.items.length ? (
            <p className="text-sm text-secondary-500">
              Nenhuma empresa com contrato ativo para avaliar.
            </p>
          ) : (
            <>
              <div className={`overflow-x-auto rounded-xl border border-secondary-200 bg-white ${loading ? 'opacity-60' : ''}`}>
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-secondary-100 bg-secondary-50 text-xs uppercase text-secondary-500">
                    <tr>
                      <th className="px-4 py-3">Empresa</th>
                      <th className="px-4 py-3">Score</th>
                      <th className="px-4 py-3">Tickets abertos</th>
                      <th className="px-4 py-3">SLA estourado</th>
                      <th className="px-4 py-3">CSAT</th>
                      <th className="px-4 py-3">NPS</th>
                      <th className="px-4 py-3">Contrato</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {data.items.map((item) => (
                      <tr key={item.companyId}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-secondary-900">{item.companyName}</p>
                        </td>
                        <td className={`px-4 py-3 text-lg font-semibold ${scoreTone(item.healthScore)}`}>
                          {item.healthScore}
                        </td>
                        <td className="px-4 py-3">{item.openTicketCount}</td>
                        <td className="px-4 py-3">{item.slaBreachCount}</td>
                        <td className="px-4 py-3">
                          {item.avgCsat != null ? `${item.avgCsat.toFixed(1)} (${item.csatCount})` : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {item.avgNps != null ? `${item.avgNps.toFixed(1)} (${item.npsCount})` : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {item.daysUntilContractEnd != null
                            ? `${item.daysUntilContractEnd} dias`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                totalCount={totalCount}
                page={page}
                pageSize={PAGE_SIZE}
                onPrevious={() => setPage((current) => Math.max(1, current - 1))}
                onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
              />
            </>
          )}
        </>
      )}
    </PageShell>
  );
}
